import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { Link } from 'react-router-dom';
import { api_error_reason } from '../sales_team/api';
import type { TeamMember } from '../sales_team/api';
import { assign_task, get_roster } from './claim_api';
import { assignable_members, unconfigured_count, unconfigured_hint } from './claim_hold';
import type { AssignResult } from './claim_hold';
import { MENU_WIDTH, menu_position } from './assign_menu_position';
import type { MenuPos } from './assign_menu_position';
import { PHONE_MAX } from '../../../utils/responsive/breakpoints';
import { useIsMobile } from '../../../utils/responsive/viewport';
import { Chip, btnStyle } from './ui';

interface Props {
  task_uuid: string;
  on_assigned: (res: AssignResult, member: TeamMember) => void;
  on_error?: (message: string) => void;
  label?: string;
}

// Below 640px the menu becomes a bottom sheet with 48px rows (matches SalesDialog).
const PHONE_SHEET_MAX = PHONE_MAX - 1;

function SearchIcon() {
  return (
    <svg aria-hidden="true" width="13" height="13" viewBox="0 0 16 16" className="shrink-0">
      <circle cx="7" cy="7" r="4.8" fill="none" stroke="currentColor" strokeWidth="1.7" />
      <path d="m10.6 10.6 3.4 3.4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}

function AssignPicker({ task_uuid, on_assigned, on_error, label = 'Assign' }: Props) {
  const [open, set_open] = useState(false);
  const [members, set_members] = useState<TeamMember[] | null>(null);
  const [load_error, set_load_error] = useState('');
  const [query, set_query] = useState('');
  const [busy, set_busy] = useState('');
  const [pos, set_pos] = useState<MenuPos | null>(null);
  const root_ref = useRef<HTMLDivElement>(null);
  const trigger_ref = useRef<HTMLButtonElement>(null);
  const search_ref = useRef<HTMLInputElement>(null);
  const option_refs = useRef<Array<HTMLButtonElement | null>>([]);
  const phone = useIsMobile(PHONE_SHEET_MAX);

  useLayoutEffect(() => {
    if (!open || phone) return undefined;
    const place = () => {
      if (trigger_ref.current) set_pos(menu_position(trigger_ref.current));
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, phone]);

  useEffect(() => {
    if (!open) return undefined;
    let live = true;
    set_load_error('');
    get_roster()
      .then((m) => live && set_members(m))
      .catch((e: unknown) => live && set_load_error(api_error_reason(e, 'Could not load reps')));
    const on_down = (e: MouseEvent) => {
      if (root_ref.current && !root_ref.current.contains(e.target as Node)) set_open(false);
    };
    const on_key = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      set_open(false);
      trigger_ref.current?.focus();
    };
    document.addEventListener('mousedown', on_down);
    document.addEventListener('keydown', on_key);
    return () => {
      live = false;
      document.removeEventListener('mousedown', on_down);
      document.removeEventListener('keydown', on_key);
    };
  }, [open]);

  const shown = useMemo(() => assignable_members(members || [], query), [members, query]);
  const unconfigured = useMemo(() => unconfigured_count(members || []), [members]);

  // ArrowDown/ArrowUp walk search box -> options; ArrowUp past the first option
  // returns to the search box.
  const focus_option = (i: number) => {
    if (i < 0) {
      search_ref.current?.focus();
      return;
    }
    option_refs.current[Math.min(i, shown.length - 1)]?.focus();
  };
  const on_search_key = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' && shown.length) {
      e.preventDefault();
      focus_option(0);
    }
  };
  const on_option_key = (e: ReactKeyboardEvent<HTMLButtonElement>, i: number) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      focus_option(i + 1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      focus_option(i - 1);
    }
  };

  const pick = (m: TeamMember) => {
    if (busy) return;
    set_busy(m.ops_uuid);
    assign_task(task_uuid, m.ops_uuid)
      .then((res) => {
        set_open(false);
        set_query('');
        trigger_ref.current?.focus();
        on_assigned(res, m);
      })
      .catch((e: unknown) => on_error?.(api_error_reason(e, 'Could not assign the task')))
      .finally(() => set_busy(''));
  };

  const close = () => {
    set_open(false);
    trigger_ref.current?.focus();
  };

  const rowClass = phone ? 'min-h-[48px] px-4' : 'px-3 py-2';
  const menu = (
    <>
      <div className={phone ? 'px-4 pb-2.5' : 'p-2.5'}>
        <div
          className={`flex items-center gap-2 rounded-lg border border-line-subtle px-2.5 text-content-muted focus-within:border-brand-primary ${
            phone ? 'h-11' : 'h-[34px]'
          }`}
          style={{ background: 'var(--input-bg)' }}
        >
          <SearchIcon />
          <input
            ref={search_ref}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            type="search"
            onKeyDown={on_search_key}
            value={query}
            onChange={(e) => set_query(e.target.value)}
            placeholder="Search reps"
            aria-label="Search reps"
            className="w-full min-w-0 text-[13px] outline-none"
            style={{
              margin: 0,
              border: 0,
              padding: 0,
              background: 'transparent',
              color: 'var(--input-text)'
            }}
          />
        </div>
      </div>
      <ul
        role="listbox"
        aria-label="Reps"
        className="m-0 min-h-0 list-none overflow-y-auto p-0 pb-1.5"
        style={phone ? undefined : { maxHeight: 260 }}
      >
        {load_error ? (
          <li className="px-3 py-2 text-xs" style={{ color: 'var(--state-error)' }}>
            {load_error}
          </li>
        ) : null}
        {!load_error && !members ? (
          <li className="px-3 py-2 text-xs text-content-muted">Loading reps…</li>
        ) : null}
        {members && shown.length === 0 ? (
          <li className="px-3 py-2 text-xs text-content-muted">No matching active reps</li>
        ) : null}
        {shown.map((m, i) => (
          <li
            key={m.ops_uuid}
            role="option"
            aria-selected={false}
            // Hover/focus tint on the li: the CMS-wide button:hover rule would beat a class on the button.
            className={`hover:bg-surface-sunken focus-within:bg-surface-sunken ${
              phone ? 'border-t border-line-subtle' : ''
            }`}
            style={{ listStyle: 'none' }}
          >
            <button
              ref={(el) => {
                option_refs.current[i] = el;
              }}
              type="button"
              disabled={!!busy}
              onClick={() => pick(m)}
              onKeyDown={(e) => on_option_key(e, i)}
              className={`flex w-full items-center gap-2 rounded-none text-left text-[13px] ${rowClass}`}
              style={{
                margin: 0,
                background: 'transparent',
                color: 'var(--text-primary)',
                border: 'none',
                cursor: busy ? 'wait' : 'pointer'
              }}
            >
              <span className="min-w-0 flex-1 truncate" title={m.name || m.email || m.ops_uuid}>
                {busy === m.ops_uuid ? 'Assigning…' : m.name || m.email || m.ops_uuid}
              </span>
              {m.level ? <Chip tone="brand">{m.level}</Chip> : null}
              {m.on_leave_today ? <Chip tone="warning">On leave</Chip> : null}
              {!m.on_shift_now ? <Chip tone="neutral">Off shift</Chip> : null}
            </button>
          </li>
        ))}
      </ul>
      {unconfigured > 0 ? (
        <div
          className={`border-t border-line-subtle text-xs ${phone ? 'px-4 py-2.5' : 'px-3 py-2'}`}
          data-testid="assign-unconfigured-hint"
        >
          <Link to="/sales-team" className="font-semibold text-content-link">
            {unconfigured_hint(unconfigured)}
          </Link>
        </div>
      ) : null}
    </>
  );

  return (
    <div ref={root_ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        ref={trigger_ref}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => set_open((o) => !o)}
        style={btnStyle('neutral', phone ? 'touch' : 'row')}
      >
        {label}
        <span aria-hidden="true" className="text-[10px] text-content-muted">
          ▾
        </span>
      </button>
      {open && phone ? (
        // Scrim + sheet stay inside root_ref so the outside-click handler treats them as inside.
        <div
          role="presentation"
          className="fixed inset-0 z-[1300] flex flex-col justify-end"
          style={{ background: 'var(--overlay-modal-strong)' }}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) close();
          }}
        >
          <div
            role="dialog"
            aria-label="Assign task"
            className="flex max-h-[78vh] flex-col rounded-t-2xl bg-surface-card text-[13px] text-content-primary"
            style={{
              position: 'fixed',
              left: 0,
              right: 0,
              bottom: 0,
              boxShadow: 'var(--shadow-modal)'
            }}
          >
            <span aria-hidden="true" className="mx-auto mt-2 h-1 w-10 rounded bg-line-subtle" />
            <div className="flex items-center px-4 pb-1.5 pt-2.5">
              <span className="text-[15px] font-semibold">Assign to</span>
              <button
                type="button"
                aria-label="Close"
                onClick={close}
                className="ml-auto text-xl leading-none text-content-muted"
                style={{ margin: 0, width: 44, height: 44, border: 0, background: 'transparent' }}
              >
                ×
              </button>
            </div>
            {menu}
            <div className="border-t border-line-subtle px-4 pb-[22px] pt-2.5">
              <button
                type="button"
                onClick={close}
                style={{ ...btnStyle('neutral', 'touch'), width: '100%' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {open && !phone ? (
        <div
          role="dialog"
          aria-label="Assign task"
          className="overflow-hidden rounded-[10px] border border-line-subtle bg-surface-card text-[13px]"
          style={{
            position: 'fixed',
            ...(pos?.bottom !== undefined ? { bottom: pos.bottom } : { top: pos?.top ?? 0 }),
            left: pos?.left ?? 0,
            visibility: pos ? 'visible' : 'hidden',
            zIndex: 1000,
            width: MENU_WIDTH,
            boxShadow: 'var(--shadow-modal)'
          }}
        >
          {menu}
        </div>
      ) : null}
    </div>
  );
}

export default AssignPicker;
